import { ref } from 'vue';
import type { PresignChunk } from '../types/PresignChunk';

interface IFile {
  file: File;
  id: string;
  name: string;
  url: string;
  status: string;
}

class UploadableFile implements IFile {
  private _file;
  private _id;
  private _name;
  private _url;
  private _status;
  private _presign;

  constructor(file: File) {
    this._file = file;
    this._id = `${file.name}-${file.size}-${file.lastModified}-${file.type}`;
    this._name = file.name;
    this._url = URL.createObjectURL(file);

    this._status = null;
    this._presign = null;
  }

  get file(): File {
    return this._file;
  }

  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get url(): string {
    return this._url;
  }

  get status(): string {
    return this._status;
  }

  set status(status: string): void {
    this._status = status;
  }

  get presign(): PresignChunk {
    return this._presign;
  }

  set presign(presign: PresignChunk): void {
    this._presign = presign;
  }
}

export const useFileList = () => {
  const files = ref<IFile[]>([]);

  function addFiles(newFiles: File[]) {
    const newUploadableFiles: IFile[] = [...newFiles]
      .map((file: File) => new UploadableFile(file))
      .filter((file: IFile) => !fileExists(file.id));
    files.value = files.value.concat(newUploadableFiles);
  }

  function fileExists(otherId: string) {
    return files.value.some(({ id }) => id === otherId);
  }

  function removeFile(file: IFile) {
    const index = files.value.indexOf(file);

    if (index > -1) files.value.splice(index, 1);
  }

  function clearFiles() {
    files.value = [];
  }

  function pushToFiles(file: IFile) {
    if (!fileExists(file.id)) {
      files.value.push(file);
    }
  }

  function filesToFormData(form: Ref, fields: []) {
    const payload = new FormData();

    fields.forEach((field: string) => {
      form.value[field].forEach((file: IFile) => {
        if (file?.file) {
          payload.append(field, file.file);
        }
      });
    });

    return payload;
  }

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    pushToFiles,
    filesToFormData,
  };
};
